/**
 * External Imports
 */
import { FC, useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom';

/**
 * Internal Imports
 */
import EmployeeAdHocList from './EmployeeAdHocList';
import EmployeeFeedback from './EmployeeFeedback';
import EmployeeIssues from './EmployeeIssues';
import EmployeeList from './EmployeeList';
import EmployeeProfile from './EmployeeProfile';
import EmployeeStoplight from './EmployeeStoplight';

import { RootState } from '../../redux/store';
import {
  setDelegatedEmployee,
  setStoplightSearchQuery,
  setTaskSearchQuery,
  setUserSection,
  clearUserSection
} from '../../redux/actions';

import { useDebounceValue } from '../../utils/debouncers';

const Employee: FC<{
  authorizedUserId: string;
}> = ({ authorizedUserId }) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState<string>('');

  const dbSearchTerm = useDebounceValue(searchTerm, 250);

  const { '*': view } = useParams();

  useEffect(() => {
    // In order to identify whether the user is in Employee or Patient section.
    // store variable: userSection
    dispatch(setUserSection('employee'));
    return () => {
      dispatch(
        setDelegatedEmployee({
          employeeName: 'Direct Reports List',
          hcaid: authorizedUserId
        })
      );
      dispatch(clearUserSection());
    };
  }, []);

  // TODO: Is this necessary?
  useEffect(() => {
    if (view?.includes('issues')) dispatch(setTaskSearchQuery(searchTerm));
    if (view?.includes('feedback')) dispatch(setTaskSearchQuery(searchTerm));
    if (view?.includes('stoplight'))
      dispatch(setStoplightSearchQuery(searchTerm));
  }, [dbSearchTerm]);

  return (
    <div>
      <Routes>
        <Route index element={<Navigate to="list" replace />} />
        <Route path="list" element={<Outlet />}>
          <Route
            path="adhoc"
            element={
              <EmployeeAdHocList
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            }
          />
          <Route
            path=":type"
            element={
              <EmployeeList
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            }
          />
        </Route>

        <Route
          path="issues"
          element={
            <EmployeeIssues
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
          }
        />

        <Route
          path="feedback"
          element={
            <EmployeeFeedback
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
          }
        />

        <Route
          path="stoplight"
          element={
            <EmployeeStoplight
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
          }
        />
        <Route path="profile" element={<EmployeeProfile />} />
      </Routes>
    </div>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUserId: state.AuthorizedUser.authorizedUser.hcaid,
    selectedDelegate: state.EmployeeReducer.selectedDelegatedEmployee
  };
};

export default connect(mapReduxStateToProps)(Employee);
